import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  IconArrowUp,
  IconArrowDown,
  IconCopy,
  IconTrash,
} from '@tabler/icons-react';
import type { CatalogProduct } from '@techgitter/contracts';
import type { TemplateLineItem } from '../../../types/ticketTypeTemplate';
import { PRICING_RULE_REGISTRY } from '../../../utils/templateEngine';
import { EditorCard } from '../EditorCard';
import { Chip } from '../../chip';
import { FIELD_SX } from '../../../styles/designSystem';

const CANCEL_BTN_SX = { color: '#888', '&:hover': { color: '#555', bgcolor: 'transparent' } };

interface TemplateLineItemEditorProps {
  item: TemplateLineItem;
  onChange: (updated: TemplateLineItem) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** Product catalog for the Line Item ID picker. Loaded once by the parent
   *  (mirrors how WorkItemExpansionPanel feeds LineItemCard). Optional so the
   *  editor still renders — as a plain free-text SKU box — when absent. */
  products?: CatalogProduct[];
}

export const TemplateLineItemEditor: React.FC<TemplateLineItemEditorProps> = ({
  item,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  products,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Catalog-backed options for the Line Item ID picker — same source and
  // shape the ticket line-item picker (LineItemCard) uses. Deduped by SKU,
  // enabled products only, sorted for a stable dropdown.
  const productBySku = useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    for (const p of products ?? []) {
      if (!p.disabled) map.set(p.sku, p);
    }
    return map;
  }, [products]);
  const productSkuOptions = useMemo(
    () => [...productBySku.keys()].sort((a, b) => a.localeCompare(b)),
    [productBySku],
  );

  const setField = <K extends keyof TemplateLineItem>(key: K, value: TemplateLineItem[K]) => {
    onChange({ ...item, [key]: value });
  };

  const setNumberField = (key: 'defaultQuantity' | 'defaultUnitCost', text: string) => {
    if (text === '') {
      if (key === 'defaultUnitCost') setField('defaultUnitCost', undefined);
      else setField('defaultQuantity', 0);
      return;
    }
    const n = Number(text);
    if (Number.isNaN(n)) return;
    setField(key, n);
  };

  const pricingRuleOptions = Object.keys(PRICING_RULE_REGISTRY);

  return (
    <>
      <EditorCard
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        title={
          <>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.lineItemId || <em style={{ color: '#aaa', fontWeight: 400 }}>(no lineItemId)</em>}
            </span>
            <span className="editor-card__id">{item.templateKey}</span>
            <Chip tone="neutral">{item.category}</Chip>
            {item.pricingRule && <Chip tone="info">{item.pricingRule}</Chip>}
          </>
        }
        actions={
          <>
            {onMoveUp && (
              <button type="button" className="editor-icon-btn" title="Move up" onClick={onMoveUp}>
                <IconArrowUp size={15} stroke={1.5} />
              </button>
            )}
            {onMoveDown && (
              <button type="button" className="editor-icon-btn" title="Move down" onClick={onMoveDown}>
                <IconArrowDown size={15} stroke={1.5} />
              </button>
            )}
            {onDuplicate && (
              <button type="button" className="editor-icon-btn" title="Duplicate" onClick={onDuplicate}>
                <IconCopy size={15} stroke={1.5} />
              </button>
            )}
            <button
              type="button"
              className="editor-icon-btn editor-icon-btn--danger"
              title="Delete line item"
              onClick={() => setDeleteConfirm(true)}
            >
              <IconTrash size={15} stroke={1.5} />
            </button>
          </>
        }
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Template Key"
            value={item.templateKey}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }}
            helperText="Set at creation"
            sx={[{ width: 240 }, FIELD_SX]}
          />
          <Autocomplete
            size="small"
            freeSolo
            autoHighlight
            options={productSkuOptions}
            value={item.lineItemId || ''}
            // onChange commits option selections / freeSolo blur/Enter and the
            // clear button; onInputChange only handles live free typing. We
            // ignore the non-'input' reasons (notably the 'reset' MUI fires on
            // mount) so simply opening a template doesn't rewrite lineItemId
            // and mark the form dirty.
            onChange={(_, v) => setField('lineItemId', v || '')}
            onInputChange={(_, v, reason) => {
              if (reason === 'input') setField('lineItemId', v);
            }}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              return options.filter((sku) => {
                if (sku.toLowerCase().includes(q)) return true;
                const product = productBySku.get(sku);
                return product?.displayName?.toLowerCase().includes(q) ?? false;
              });
            }}
            renderOption={(props, option) => {
              const product = productBySku.get(option);
              return (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {option}
                    </Typography>
                    {product?.displayName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block' }}
                      >
                        {product.displayName}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            sx={[{ flex: 1 }, FIELD_SX]}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Line Item ID"
                required
                helperText="Product SKU / billing code — type to search or pick from the catalog"
                sx={{ '& input': { fontFamily: 'monospace' } }}
              />
            )}
          />
          <TextField
            size="small"
            label="Category"
            value={item.category}
            onChange={(e) => setField('category', e.target.value)}
            sx={[{ flex: 1 }, FIELD_SX]}
            helperText="Line-item category (e.g. labour, product, licence)"
            required
          />
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Default Quantity"
            value={String(item.defaultQuantity)}
            onChange={(e) => setNumberField('defaultQuantity', e.target.value)}
            type="number"
            sx={[{ flex: 1 }, FIELD_SX]}
            helperText="Pre-filled quantity (hours or units per the template's Billing Mode)"
            required
          />
          <TextField
            size="small"
            label="Default Unit Cost"
            value={item.defaultUnitCost === undefined ? '' : String(item.defaultUnitCost)}
            onChange={(e) => setNumberField('defaultUnitCost', e.target.value)}
            type="number"
            sx={[{ flex: 1 }, FIELD_SX]}
            helperText="Optional — pricing rule may override"
          />
          <Autocomplete
            size="small"
            freeSolo
            options={pricingRuleOptions}
            value={item.pricingRule ?? ''}
            onChange={(_, v) => setField('pricingRule', v || undefined)}
            onInputChange={(_, v) => setField('pricingRule', v || undefined)}
            sx={[{ flex: 1 }, FIELD_SX]}
            renderInput={(params) => (
              <TextField {...params} label="Pricing rule" helperText="Function name from registry" />
            )}
          />
        </Stack>

        <TextField
          size="small"
          label="Prefix Template"
          value={item.prefixTemplate ?? ''}
          onChange={(e) => setField('prefixTemplate', e.target.value || undefined)}
          fullWidth
          helperText="Supports {fieldKey} interpolation"
          sx={FIELD_SX}
        />
        <TextField
          size="small"
          label="Line Detail Template"
          value={item.lineDetailTemplate ?? ''}
          onChange={(e) => setField('lineDetailTemplate', e.target.value || undefined)}
          fullWidth
          multiline
          minRows={2}
          helperText="Supports {fieldKey} and {?conditionalBlock}...{/conditionalBlock}"
          sx={FIELD_SX}
        />

        <Stack direction="row" spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={!!item.afterHours}
                onChange={(e) => setField('afterHours', e.target.checked || undefined)}
              />
            }
            label="After hours"
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!item.nonChargeable}
                onChange={(e) => setField('nonChargeable', e.target.checked || undefined)}
              />
            }
            label="Non-chargeable"
          />
        </Stack>
      </EditorCard>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Line Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete line item "{item.templateKey}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={CANCEL_BTN_SX}>Cancel</Button>
          <Button
            onClick={() => {
              setDeleteConfirm(false);
              onDelete();
            }}
            color="error"
            variant="contained"
            disableElevation
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
